/**
 * Removing a deck from a study group, settled by the group.
 *
 * The removal is optimistic — the deck leaves this device's group view at
 * once — and the group answers it:
 *   - `op-ack`      → it landed: drop this device's link to the group. The
 *                     group feed's `deck_removed` is written by the group
 *                     itself, once, when the removal lands — never by a
 *                     device (web and phone removing the same deck, or a
 *                     re-sent removal, would each log it again).
 *   - `op-rejected` → not removed: put the deck back, as the newest copy of
 *                     the row this device holds (the engine raises the
 *                     rejection toast). Either the sender is not the member
 *                     who shared it, or the removal is older than the deck's
 *                     last write (`stale-lww`: renamed or moved after the
 *                     removal was stamped, e.g. a removal re-sent after the
 *                     app was killed offline). The group then keeps the deck
 *                     WHOLE — a removal never empties a deck it did not
 *                     remove — and the sharer, seeing it back, removes it
 *                     again. It is not re-issued on its own: the edit that
 *                     beat it may be the reason the deck should stay.
 *   - no answer     → NOT a refusal. A frame written to a socket that is down
 *                     is lost without an error, so the removal stays pending
 *                     (the deck stays hidden, a re-shipped row does not bring
 *                     it back) and is sent again on the group's next sync
 *                     (`resendPendingDeckRemovals`, from the dirty flush).
 *
 * The pending entry lives in the group's slice (`pendingRemovals`), which is
 * persisted with the group snapshot, so an app killed offline still re-sends
 * the removal on its next sync. Re-sending is safe: the group's delete is
 * idempotent, and a removal that finds the deck already removed is acked
 * without a second feed entry.
 */
import type { SGDeckProps } from '@flashka/shared/sync-engine/schema/group-entities';
import type {
  SyncCardProps,
  SyncDeckMeta,
} from '@flashka/shared/sync-engine/schema/shared-types';

import { enqueueForGroup } from '../internal/hook/study-group-sender-registry';
import { useFlashkaStore } from '../internal/store/flashka-store';
import {
  type PendingDeckRemoval,
  useStudyGroupStore,
} from '../internal/store/study-group-store';
import {
  generateMutationId,
  type SettlementOutcome,
} from '../internal/sync-bookkeeping/pending-mutations';
import { getPendingMutationsTracker } from '../internal/sync-bookkeeping/pending-mutations-singleton';

type Transport = (raw: string, onSent: () => void) => void;

const key = (groupId: string, deckId: string) => `${groupId}\u0001${deckId}`;

/** In-flight mutation id per removal (in memory; a restart re-sends fresh). */
const inFlight = new Map<string, string>();
/**
 * Removals for a group whose slice is not on this device (e.g. removed from
 * the personal deck's delete dialog before the group ever synced here). They
 * cannot be persisted without minting a slice over the group's snapshot, so
 * they live for the session only — the outbox still delivers them once the
 * group's socket connects.
 */
const detached = new Map<string, PendingDeckRemoval>();
/** The removed deck's cards, to put back on a refusal (not persisted —
 *  a refused deck with no bucket is loaded on demand like any other). */
const removedCards = new Map<string, { cards: SyncCardProps[]; wasLoaded: boolean }>();

function readPending(groupId: string, deckId: string): PendingDeckRemoval | undefined {
  return (
    useStudyGroupStore.getState().groups[groupId]?.pendingRemovals?.[deckId] ??
    detached.get(key(groupId, deckId))
  );
}

function clearPending(groupId: string, deckId: string): void {
  useStudyGroupStore.getState().setPendingDeckRemoval(groupId, deckId, null);
  detached.delete(key(groupId, deckId));
  removedCards.delete(key(groupId, deckId));
}

/**
 * Remove `deckId` from the group on this device and send the removal.
 * Returns the mutation id of the first attempt.
 */
export function removeGroupDeck(groupId: string, deckId: string, userId: string): string {
  // Already being removed from this device: the first removal stands — its
  // clock, and the copy of the deck a refusal puts back (the deck is hidden
  // now, so a second entry would hold nothing to restore). Re-send it only
  // if no attempt is in flight.
  const pending = readPending(groupId, deckId);
  if (pending) {
    const mid = inFlight.get(key(groupId, deckId));
    if (mid && getPendingMutationsTracker().inspect(mid)?.kind === 'pending') return mid;
    return send(groupId, deckId, pending, (raw, onSent) =>
      enqueueForGroup(groupId, raw, onSent),
    );
  }
  const store = useStudyGroupStore.getState();
  const group = store.groups[groupId];
  const row = group?.deckProps[deckId];
  const now = removalClock(row?.updatedAtISO);
  const cards = group?.cards[deckId];
  if (cards) {
    removedCards.set(key(groupId, deckId), {
      cards: Object.values(cards),
      wasLoaded: group?.loadedDeckIds.has(deckId) ?? false,
    });
  }
  const entry: PendingDeckRemoval = {
    deletedTimeISO: now,
    userId,
    ...(row ? { row } : {}),
  };
  if (group) store.setPendingDeckRemoval(groupId, deckId, entry);
  else detached.set(key(groupId, deckId), entry);
  store.removeDeck(groupId, deckId);
  return send(groupId, deckId, entry, (raw, onSent) =>
    enqueueForGroup(groupId, raw, onSent),
  );
}

/**
 * Is this device's removal of `deckId` from the group still waiting for the
 * group's answer? The delete dialogs do not offer that group again meanwhile.
 */
export function isGroupDeckRemovalPending(groupId: string, deckId: string): boolean {
  return readPending(groupId, deckId) !== undefined;
}

/**
 * The removal's clock: now, but never at or before the newest write of the row
 * this device has seen. The group applies the removal only if it is newer than
 * the row, so a device whose clock runs behind the one that last wrote the row
 * would otherwise have every removal refused.
 */
function removalClock(rowUpdatedAtISO: string | undefined): string {
  const now = Date.now();
  const seen = rowUpdatedAtISO ? Date.parse(rowUpdatedAtISO) : NaN;
  return new Date(Number.isFinite(seen) && seen >= now ? seen + 1 : now).toISOString();
}

/**
 * Re-send every removal of this group the group has not answered. Called by
 * the group's dirty flush once a (re)sync has landed — after the diff, so the
 * re-shipped rows are already masked.
 */
export function resendPendingDeckRemovals(
  groupId: string,
  sendRaw: (raw: string) => void,
): void {
  const inSlice = useStudyGroupStore.getState().groups[groupId]?.pendingRemovals ?? {};
  const all: Array<[string, PendingDeckRemoval]> = Object.entries(inSlice);
  for (const [k, entry] of detached) {
    const [g, deckId] = k.split('\u0001');
    if (g === groupId && deckId && !(deckId in inSlice)) all.push([deckId, entry]);
  }
  const tracker = getPendingMutationsTracker();
  const transport: Transport = (raw, onSent) => {
    sendRaw(raw);
    onSent();
  };
  for (const [deckId, entry] of all) {
    const mid = inFlight.get(key(groupId, deckId));
    if (mid && tracker.inspect(mid)?.kind === 'pending') {
      // Same attempt, sent again: whichever copy the group answers settles it.
      sendRaw(JSON.stringify(frame(deckId, entry, mid)));
      continue;
    }
    send(groupId, deckId, entry, transport);
  }
}

function frame(deckId: string, entry: PendingDeckRemoval, mutationId: string) {
  return {
    type: 'sg-deck-delete',
    userId: entry.userId,
    deckId,
    deletedTimeISO: entry.deletedTimeISO,
    mutationId,
  };
}

function send(
  groupId: string,
  deckId: string,
  entry: PendingDeckRemoval,
  transport: Transport,
): string {
  const tracker = getPendingMutationsTracker();
  const mutationId = generateMutationId();
  tracker.register(mutationId, ['shared']);
  inFlight.set(key(groupId, deckId), mutationId);
  tracker.onSettled(mutationId, (outcome) =>
    settle(groupId, deckId, mutationId, outcome),
  );
  transport(JSON.stringify(frame(deckId, entry, mutationId)), () =>
    tracker.markSent(mutationId),
  );
  return mutationId;
}

function settle(
  groupId: string,
  deckId: string,
  mutationId: string,
  outcome: SettlementOutcome | null,
): void {
  if (inFlight.get(key(groupId, deckId)) === mutationId) {
    inFlight.delete(key(groupId, deckId));
  }
  // Timeout / unknown: not an answer — stays pending, re-sent on the next sync.
  if (!outcome || outcome.kind === 'timeout' || outcome.kind === 'pending') return;
  const entry = readPending(groupId, deckId);
  if (!entry) return; // already settled by another attempt
  const cards = removedCards.get(key(groupId, deckId));
  clearPending(groupId, deckId);
  if (outcome.kind === 'acked') accepted(groupId, deckId);
  else refused(groupId, deckId, entry.row, cards);
}

function refused(
  groupId: string,
  deckId: string,
  row: SGDeckProps | undefined,
  cards: { cards: SyncCardProps[]; wasLoaded: boolean } | undefined,
): void {
  if (!row) return;
  const store = useStudyGroupStore.getState();
  store.mergeDeckProps(groupId, [row]);
  if (!cards || store.groups[groupId]?.cards[deckId]) return;
  if (cards.wasLoaded) store.setDeckCards(groupId, deckId, cards.cards);
  else store.mergeCards(groupId, deckId, cards.cards);
}

function accepted(groupId: string, deckId: string): void {
  // A re-sync may have re-shipped cards under the hidden deck: drop them.
  // (No `deck_removed` from here: the group logs it when the removal lands.)
  useStudyGroupStore.getState().removeDeck(groupId, deckId);
  // Drop the link from any local deck pointing at this SG deck. Without this
  // the owner's UserDo `linkedGroups` still includes (groupId, sgDeckId), so
  // the server-side `propagateSharedFieldsToLinkedGroups` keeps fanning every
  // card edit to the group the deck just left. (The group DO also unlinks
  // every member on commit — this is the local echo.)
  const store = useFlashkaStore.getState();
  const allProps = store.deckProperties as Record<string, SyncDeckMeta>;
  for (const [localId, props] of Object.entries(allProps)) {
    const links = props.linkedGroups ?? [];
    const filtered = links.filter(
      (l) => !(l.groupId === groupId && l.sgDeckId === deckId),
    );
    if (filtered.length === links.length) continue;
    store.setDeckProperty(localId, { ...props, linkedGroups: filtered });
  }
}
